import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    PickerItemData,
    PickerSectionData,
} from "@design-system/Picker/Picker.types";
import { BottomArrowIcon } from "@icons";
import {
    type KeyboardEvent,
    ReactNode,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import styles from "./DropdownCell.module.scss";
import { DropdownOption } from "./dropdownOption";
import { groupDropdownOptions } from "./dropdownOptionGroups";
import { BaseCellProps } from "./index";
import { stopPropagation, stopPropagationExceptTab } from "./cellEditorEvents";

interface DropdownCellProps<
    T extends string | number = string,
> extends BaseCellProps<T> {
    readonly options?: Array<DropdownOption<T>>;
    readonly renderDisplay?: (value: T | null) => ReactNode;
    readonly editorClassName?: string;
    readonly displayClassName?: string;
    readonly setSearch?: (search: string) => void;
    readonly showDropdownIndicator?: boolean;
    readonly cta?:
        | ReactNode
        | ((search: string, closePopover: () => void) => ReactNode);
}

interface DropdownCellEditorProps<T extends string | number> {
    readonly value: T | null;
    readonly finishEditing: (value?: T | null) => void;
    readonly selectOptions: Array<DropdownOption<T>>;
    readonly editorClassName?: string;
    readonly setSearch?: (search: string) => void;
    readonly enterEditModeKey: string | null;
    readonly showDropdownIndicator?: boolean;
    readonly cta?:
        | ReactNode
        | ((search: string, closePopover: () => void) => ReactNode);
}

function getOptionSearchLabel<T extends string | number>(
    option: DropdownOption<T>,
): string {
    if (option.searchableLabel) {
        return option.searchableLabel;
    }
    if (typeof option.label === "string") {
        return option.label;
    }
    return String(option.value);
}

const getOptionLabel = <T extends string | number>(
    option: DropdownOption<T>,
): string =>
    typeof option.label === "string"
        ? option.label
        : getOptionSearchLabel(option);

interface DropdownPickerOptions<T extends string | number> {
    items?: Array<PickerItemData<T>>;
    sections?: Array<PickerSectionData<T>>;
    labelByValue: ReadonlyMap<T, string>;
    matchesQuery: (item: PickerItemData<T>, query: string) => boolean;
}

/**
 * Picker rows for dropdown options: sections when any option carries a group
 * header, text labels (Picker rows take strings), and a filter matching the
 * options' searchable labels.
 */
const toDropdownPickerOptions = <T extends string | number>(
    options: Array<DropdownOption<T>>,
): DropdownPickerOptions<T> => {
    const toItem = (option: DropdownOption<T>): PickerItemData<T> => ({
        value: option.value,
        label: getOptionLabel(option),
        rightText:
            typeof option.suffix === "string" ? option.suffix : undefined,
        disabled: option.disabled,
    });
    const searchLabelByValue = new Map(
        options.map((option) => [
            option.value,
            getOptionSearchLabel(option).toLowerCase(),
        ]),
    );
    const matchesQuery = (item: PickerItemData<T>, query: string) =>
        query.trim() === "" ||
        (
            searchLabelByValue.get(item.value) ?? item.label.toLowerCase()
        ).includes(query.toLowerCase());
    const groups = groupDropdownOptions(options);
    const hasSections = groups.some((group) => group.header != null);

    return {
        ...(hasSections
            ? {
                  sections: groups.map((group) => ({
                      key: group.key,
                      title:
                          typeof group.header === "string"
                              ? group.header
                              : undefined,
                      items: group.options.map(toItem),
                  })),
              }
            : { items: options.map(toItem) }),
        labelByValue: new Map(
            options.map((option) => [option.value, getOptionLabel(option)]),
        ),
        matchesQuery,
    };
};

/**
 * The open editor of a dropdown cell: a chromeless text trigger that filters
 * the options (or hands the text to `setSearch`). The grid owns edit mode, so
 * every key but Tab and every click stays inside the editor, a pick (Tab
 * included) commits through `finishEditing(value)` and any close (Escape,
 * outside press, focus leaving) hands control back to the grid.
 */
const DropdownCellEditor = <T extends string | number>({
    value,
    finishEditing,
    selectOptions,
    editorClassName,
    setSearch,
    enterEditModeKey,
    showDropdownIndicator,
    cta,
}: DropdownCellEditorProps<T>) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isCleared, setIsCleared] = useState(false);
    const hasArrowedSinceClearRef = useRef(false);
    const isExternalSearch = setSearch !== undefined;
    const isStartedByTyping = enterEditModeKey?.length === 1;

    const setSearchRef = useRef(setSearch);
    useLayoutEffect(() => {
        setSearchRef.current = setSearch;
    });
    // Once per edit session, so a new `setSearch` identity on a parent
    // re-render never clears the typed text. Edit mode starts inside the
    // opening keydown and this commit runs before that key's keypress, so a
    // printable key lands in the focused input natively, replacing the
    // selected label. Seeding the key instead and focusing later moves focus
    // while the key is still in flight, which lets the browser insert it
    // again (or, under a slow mount, keep re-dispatching it into the input).
    useLayoutEffect(() => {
        setSearchRef.current?.("");
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    // Enter or a double click shows the selected label; select it once it has
    // rendered so typing replaces it.
    useEffect(() => {
        if (isStartedByTyping) {
            return;
        }
        const frame = requestAnimationFrame(() => inputRef.current?.select());
        return () => cancelAnimationFrame(frame);
    }, [isStartedByTyping]);

    const pickerOptions = useMemo(
        () => toDropdownPickerOptions(selectOptions),
        [selectOptions],
    );
    const selectedLabel =
        value === null ? "" : (pickerOptions.labelByValue.get(value) ?? "");

    // An untouched input shows the selected label; that text is not a search.
    const toSearchText = (query: string) =>
        query.trim() === selectedLabel.trim() ? "" : query;

    const closePopover = () => finishEditing();

    // Emptying the input drops the selection, and Enter or Tab on the empty
    // input saves the cleared cell; Escape and clicking away keep the value.
    // Base UI keeps the old row highlighted after the clear, so Enter is
    // caught before it unless the user arrowed to a row since clearing.
    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            hasArrowedSinceClearRef.current = true;
            return;
        }
        const isRemoving =
            isCleared &&
            inputRef.current?.value === "" &&
            (event.key === "Tab" ||
                (event.key === "Enter" && !hasArrowedSinceClearRef.current));
        if (!isRemoving) {
            return;
        }
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
        }
        finishEditing(null);
    };

    return (
        <div
            className={editorClassName ?? styles.dropdownCellEditor}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onKeyDownCapture={handleKeyDownCapture}
            onKeyDown={stopPropagationExceptTab}
        >
            <Picker.Select
                items={pickerOptions.items}
                sections={pickerOptions.sections}
                trigger="cellInput"
                cellEditor={{ openingKey: enterEditModeKey }}
                ref={inputRef}
                chevron={showDropdownIndicator ? "always" : "none"}
                className={styles.cellInput}
                placeholder="Search..."
                value={isCleared ? null : value}
                defaultOpen
                filterable={!isExternalSearch}
                filter={pickerOptions.matchesQuery}
                emptyLabel="No matching items"
                onInputValueChange={(query) => setSearch?.(toSearchText(query))}
                onValueChange={(next) => {
                    if (next === null) {
                        hasArrowedSinceClearRef.current = false;
                        setIsCleared(true);
                        return;
                    }
                    finishEditing(next);
                }}
                onOpenChange={(open) => {
                    if (!open) {
                        finishEditing();
                    }
                }}
                renderFooter={
                    cta === undefined
                        ? undefined
                        : (query) =>
                              typeof cta === "function"
                                  ? cta(toSearchText(query), closePopover)
                                  : cta
                }
            />
        </div>
    );
};

export const DropdownCell = <T extends string | number = string>({
    value,
    isEditing,
    finishEditing,
    options,
    renderDisplay,
    editorClassName,
    displayClassName,
    setSearch,
    enterEditModeKey,
    showDropdownIndicator,
    cta,
}: DropdownCellProps<T>) => {
    const selectOptions = useMemo<Array<DropdownOption<T>>>(
        () => options ?? [],
        [options],
    );

    const defaultRenderDisplay = (val: T | null): ReactNode => {
        const option =
            val !== null
                ? selectOptions.find((opt) => opt.value === val)
                : null;

        return (
            <div className={styles.dropdownDisplay}>
                {option && (
                    <BreakableText
                        text={String(option.label)}
                        showTooltip
                        className={styles.dropdownLabel}
                    />
                )}
                {showDropdownIndicator && (
                    <BottomArrowIcon className={styles.dropdownIcon} />
                )}
            </div>
        );
    };

    const displayRenderer = renderDisplay ?? defaultRenderDisplay;

    if (isEditing) {
        return (
            <DropdownCellEditor
                value={value}
                finishEditing={finishEditing}
                selectOptions={selectOptions}
                editorClassName={editorClassName}
                setSearch={setSearch}
                enterEditModeKey={enterEditModeKey}
                showDropdownIndicator={showDropdownIndicator}
                cta={cta}
            />
        );
    }

    return (
        <div className={displayClassName ?? styles.dropdownCell}>
            {displayRenderer(value)}
        </div>
    );
};
